import { cn } from "@/lib/utils";
import {
	AlertTriangle,
	CheckCircle,
	Loader2,
	RotateCcw,
	Save,
	Trash2,
	XCircle,
} from "lucide-react";
import type { ResetMutation } from "./settings-types";

// ============================================================
// Settings — Shared UI Helpers
// ============================================================

export function SaveButton({
	isPending,
	isSuccess,
	onClick,
}: { isPending: boolean; isSuccess: boolean; onClick: () => void }) {
	return (
		<div className="flex items-center gap-2">
			<button
				type="button"
				disabled={isPending}
				onClick={onClick}
				className="btn-primary text-xs !py-1.5 !px-3"
			>
				{isPending ? (
					<Loader2 size={12} className="animate-spin inline mr-1" />
				) : (
					<Save size={12} className="inline mr-1" />
				)}
				Speichern
			</button>
			{isSuccess && (
				<span className="text-xs text-green-600 flex items-center gap-1">
					<CheckCircle size={12} />
					Gespeichert
				</span>
			)}
		</div>
	);
}

export function CardSkeleton() {
	return (
		<div className="card animate-pulse space-y-3">
			<div className="h-4 bg-stone-200 rounded w-1/3" />
			<div className="h-8 bg-stone-100 rounded" />
			<div className="h-8 bg-stone-100 rounded" />
		</div>
	);
}

export function NumberField({
	label,
	value,
	onChange,
}: { label: string; value: number; onChange: (v: number) => void }) {
	return (
		<label className="block">
			<span className="text-xs font-medium text-stone-500 block mb-1">
				{label}
			</span>
			<input
				type="number"
				min={1}
				max={90}
				value={value}
				onChange={(e) => onChange(Number(e.target.value))}
				className="input text-sm w-24"
			/>
		</label>
	);
}

export function ToggleRow({
	label,
	checked,
	onChange,
	disabled,
}: {
	label: string;
	checked: boolean;
	onChange: (v: boolean) => void;
	disabled?: boolean;
}) {
	return (
		<label
			className={`flex items-center justify-between text-sm ${disabled ? "text-stone-400 cursor-not-allowed" : "text-stone-700 cursor-pointer"}`}
		>
			<span>{label}</span>
			<input
				type="checkbox"
				checked={checked}
				onChange={(e) => onChange(e.target.checked)}
				className="rounded border-stone-300"
				disabled={disabled}
			/>
		</label>
	);
}

export function ResetSection<T extends string>({
	service,
	confirmReset,
	setConfirmReset,
	mutation,
	label,
}: {
	service: T;
	confirmReset: T | null;
	setConfirmReset: (v: T | null) => void;
	mutation: ResetMutation;
	label: string;
}) {
	const isConfirming = confirmReset === service;

	return (
		<div className="border-t border-stone-100 pt-2">
			{!isConfirming ? (
				<button
					type="button"
					onClick={() => setConfirmReset(service)}
					className="text-xs text-stone-400 hover:text-red-500 flex items-center gap-1 transition-colors"
				>
					<RotateCcw size={10} />
					Zurücksetzen
				</button>
			) : (
				<div className="space-y-2">
					<p className="text-xs text-red-600 font-medium">{label}</p>
					<p className="text-xs text-stone-500">
						Danach kannst du frisch synchronisieren.
					</p>
					<div className="flex items-center gap-2">
						<button
							type="button"
							disabled={mutation.isPending}
							onClick={() => mutation.mutate()}
							className="text-xs px-2.5 py-1 rounded-md bg-red-50 text-red-700 hover:bg-red-100 font-medium transition-colors"
						>
							{mutation.isPending ? (
								<Loader2 size={10} className="animate-spin inline mr-1" />
							) : (
								<Trash2 size={10} className="inline mr-1" />
							)}
							Jetzt zurücksetzen
						</button>
						<button
							type="button"
							onClick={() => setConfirmReset(null)}
							className="text-xs text-stone-400 hover:text-stone-600"
						>
							Abbrechen
						</button>
					</div>
					{mutation.isSuccess && (
						<p className="text-xs text-green-600 flex items-center gap-1">
							<CheckCircle size={12} />
							{mutation.data?.data?.message ?? "Erfolgreich zurückgesetzt"}
						</p>
					)}
				</div>
			)}
		</div>
	);
}

export function SyncInfoRow({
	label,
	value,
}: { label: string; value: string }) {
	return (
		<div className="flex justify-between">
			<span className="text-stone-500">{label}</span>
			<span className="text-stone-700 font-medium">{value}</span>
		</div>
	);
}

export function SyncError({ message }: { message: string }) {
	return (
		<div className="bg-red-50 border border-red-100 rounded-md px-2.5 py-1.5 flex items-start gap-1.5">
			<AlertTriangle size={12} className="text-red-500 mt-0.5 shrink-0" />
			<p className="text-xs text-red-700">{message}</p>
		</div>
	);
}

export function SyncMutationError({ error }: { error: Error | null }) {
	if (!error) return null;
	return (
		<p className="text-xs text-red-600 flex items-center gap-1">
			<XCircle size={12} />
			{error instanceof Error ? error.message : "Sync fehlgeschlagen"}
		</p>
	);
}

export function StatusBadge({ configured }: { configured: boolean }) {
	return (
		<span
			className={cn(
				"badge text-xs",
				configured
					? "bg-green-50 text-green-700"
					: "bg-stone-100 text-stone-500",
			)}
		>
			{configured ? "Aktiv" : "Nicht konfiguriert"}
		</span>
	);
}
